import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Bell, Package, ClipboardText, CheckCircle, Checks } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

const NOTIF_ICONS = {
  stok_rendah: { icon: Package, color: "text-amber-500", bg: "bg-amber-50" },
  laporan_baru: { icon: ClipboardText, color: "text-blue-500", bg: "bg-blue-50" },
  default: { icon: Bell, color: "text-slate-500", bg: "bg-slate-50" },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Baru saja";
  if (mins < 60) return `${mins} menit lalu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} jam lalu`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} hari lalu`;
  return new Date(dateStr).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(data.count);
    } catch { /* ignore */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=20");
      setNotifications(data.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  // Poll unread count every 30s
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Load notifications when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
        >
          <Bell size={20} weight={unreadCount > 0 ? "fill" : "regular"} />
          {unreadCount > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3
            className="text-sm font-semibold text-[#1A1A1A]"
            style={{ fontFamily: "Work Sans" }}
          >
            Notifikasi
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read-btn"
              className="text-xs text-[#B8860B] hover:text-[#9A7209] h-7 px-2"
            >
              <Checks size={14} className="mr-1" />
              Tandai semua dibaca
            </Button>
          )}
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[400px]">
          {loading && notifications.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">Memuat...</div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center">
              <Bell size={28} className="mx-auto text-slate-300 mb-2" />
              <p className="text-xs text-slate-400">Belum ada notifikasi</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {notifications.map((notif) => {
                const cfg = NOTIF_ICONS[notif.tipe] || NOTIF_ICONS.default;
                const IconComp = cfg.icon;
                return (
                  <div
                    key={notif._id}
                    data-testid={`notification-item-${notif._id}`}
                    className={`flex gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer ${
                      !notif.is_read ? "bg-[#B8860B]/[0.03]" : ""
                    }`}
                    onClick={() => !notif.is_read && handleMarkRead(notif._id)}
                  >
                    <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                      <IconComp size={16} weight="duotone" className={cfg.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs leading-snug ${!notif.is_read ? "font-semibold text-[#1A1A1A]" : "text-slate-600"}`}>
                          {notif.judul}
                        </p>
                        {!notif.is_read && (
                          <span className="w-2 h-2 rounded-full bg-[#B8860B] shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{notif.pesan}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-100 text-center">
            <p className="text-[10px] text-slate-400">
              {unreadCount > 0 ? `${unreadCount} belum dibaca` : "Semua sudah dibaca"}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
